import { LEAVE_ROOM, SET_ROOM_JOINED } from '../../room';
import { ReducerRegistry } from '../redux';
import { ADD_FILE, ADD_MESSAGE, ADD_MESSAGE_LIST, SET_CHOICE_MEMBER, SET_IS_CHAT_READ, SET_IS_KEYBOARD_OPEN, SET_IS_LIVE, SWITCH_TO_LIVE_MESSAGE } from './actionTypes';
import { getChatSize } from './constants';
const DEFAULT_STATE = {
    messages: new Map(),
    liveMessages: new Map(),
    fileList: new Map(),
    choiceMembers: new Map(),
    isRead: true,
    totalCount: 0,
    lastMessage: undefined,
    checkTime: 0,
    isKeyboardOpen: false,
    isLive: false,
    windowSlid: false,
};
const sortMessagesByTimestamp = (messages) => {
    const sortedMessages = new Map([...messages.entries()].sort((a, b) => Number(a[1].timestamp) - Number(b[1].timestamp)));
    return sortedMessages;
};
// 실시간(WS)과 서버 조회(REST)가 같은 메시지를 서로 다른 키/ID 체계로 들여온다
// (uuid: 송신시각_user_uuid vs 서버 저장시각_from_user_id, from: user_uuid vs member_id).
// 키(uuid) 매칭만으로는 못 걸러내므로, 병합 지점에서 (보낸이+본문+시각 근접) 기준으로 억제한다.
const DEDUP_WINDOW_MS = 3000;
const isSameChatMessage = (a, b) => {
    if (a.message !== b.message || a.type !== b.type)
        return false;
    if (Math.abs(Number(a.timestamp) - Number(b.timestamp)) > DEDUP_WINDOW_MS)
        return false;
    // ★같은 from = 같은 소스의 정당한 별개 메시지(같은 텍스트 연속 전송) — 중복 아님.
    // 같은 소스의 진짜 중복은 uuid(Map 키)가 같아 이미 병합된다.
    if (a.from && b.from && a.from === b.from)
        return false;
    // 교차 소스 쌍(실시간 user_uuid vs 서버조회 member_id — from 이 항상 불일치)만
    // 닉네임+역할로 동일 메시지 여부를 판정한다.
    return a.fromNickname === b.fromNickname && a.fromRole === b.fromRole;
};
const dedupNearDuplicates = (messages) => {
    const sorted = [...messages.values()].sort((a, b) => Number(a.timestamp) - Number(b.timestamp));
    const kept = [];
    for (const msg of sorted) {
        let dup = false;
        // 시각 정렬 상태이므로 창(3s) 안의 직전 항목들만 비교하면 된다
        for (let i = kept.length - 1; i >= 0; i--) {
            if (Number(msg.timestamp) - Number(kept[i].timestamp) > DEDUP_WINDOW_MS)
                break;
            if (isSameChatMessage(kept[i], msg)) {
                dup = true;
                // 내 메시지 표식은 어느 소스로 들어왔든 보존
                if (msg.isMe && !kept[i].isMe)
                    kept[i] = { ...kept[i], isMe: true };
                break;
            }
        }
        if (!dup)
            kept.push(msg);
    }
    return new Map(kept.map(m => [m.uuid, m]));
};
ReducerRegistry.register('features/base/chat', (state = DEFAULT_STATE, action) => {
    // 성능: 과거 모든 액션에서 전체 상태(messages/liveMessages 맵 포함)를 _.cloneDeep 했으나
    // 실제 클론이 필요한 케이스는 ADD_FILE 뿐이었다 — 표적 복사로 전환(입장 러시 메인스레드 비용 제거).
    const chatSize = getChatSize();
    switch (action.type) {
        case SET_ROOM_JOINED:
            if (action.joined) {
                return {
                    ...state,
                    checkTime: new Date().getTime(),
                };
            }
            break;
        case ADD_FILE: {
            const fileList = new Map(state.fileList);
            fileList.set(action.data.name, action.data);
            return {
                ...state,
                fileList,
            };
        }
        case 'UPDATE_MESSAGE': {
            const newMessages = new Map(state.messages);
            const message = action.messages;
            // 발신 시점의 낙관적 항목(클라이언트 시각 키)을 서버 확정본(서버 시각 키)으로 교체
            if (action.replaceUuid && action.replaceUuid !== message.uuid) {
                newMessages.delete(action.replaceUuid);
            }
            newMessages.set(message.uuid, message);
            let updatedMessages = sortMessagesByTimestamp(newMessages);
            // Keep the last 50 messages if isUp is false
            const allMessages = Array.from(updatedMessages.values());
            const limitedMessages = allMessages.slice(-chatSize);
            updatedMessages = new Map(limitedMessages.map(message => [message.uuid, message]));
            // liveMessages 의 낙관적 항목도 교체 — SWITCH_TO_LIVE 병합 시 키 이원화로 중복되는 것 방지
            const newLiveMessages = new Map(state.liveMessages);
            if (action.replaceUuid && action.replaceUuid !== message.uuid) {
                newLiveMessages.delete(action.replaceUuid);
            }
            newLiveMessages.set(message.uuid, message);
            while (newLiveMessages.size > chatSize) {
                const oldestKey = newLiveMessages.keys().next().value;
                newLiveMessages.delete(oldestKey);
            }
            return {
                ...state,
                messages: updatedMessages,
                liveMessages: newLiveMessages,
                lastMessage: message,
                // 에코는 낙관 항목의 키 교체일 뿐 "라이브 복귀" 신호가 아니다 — 강제로 켜면
                // 위로 읽는 중 도착한 에코가 늦은 과거페이지 응답을 오폐기시키고, 이후 수신
                // 메시지가 읽던 윈도우를 갉아먹는다. (바닥 발신은 ADD_MESSAGE isMine 이 켬)
                isLive: state.isLive,
            };
        }
        case ADD_MESSAGE: {
            // liveMessages에 새 메시지를 추가하고, chatSize를 초과하면 가장 오래된 메시지를 삭제
            const liveMessages = new Map(state.liveMessages);
            liveMessages.set(action.message.uuid, action.message);
            while (liveMessages.size > chatSize) {
                const oldestKey = liveMessages.keys().next().value;
                liveMessages.delete(oldestKey);
            }
            // live 상태이거나 '내가 보낸 메시지'면 표시 목록(messages)에도 추가.
            // (내 메시지는 위로 스크롤한 상태/듀얼모니터 외부 창에서도 항상 보여야 함)
            const isMine = !!action.message.isMe;
            let messages = state.messages;
            if (state.isLive || isMine) {
                messages = new Map(state.messages);
                messages.set(action.message.uuid, action.message);
                // 직전 서버 조회(REST)분과 실시간 수신분이 겹치는 경계 케이스 중복 억제
                messages = dedupNearDuplicates(messages);
                while (messages.size > chatSize) {
                    const oldestKey = messages.keys().next().value;
                    messages.delete(oldestKey);
                }
            }
            return {
                ...state,
                liveMessages,
                messages,
                totalCount: state.totalCount + 1,
                lastMessage: action.message,
                isLive: isMine ? true : state.isLive,
            };
        }
        case ADD_MESSAGE_LIST: {
            let updatedMessages;
            // 위로 페이징 트림으로 "최신쪽 블록"이 실제로 폐기됐는지 — 폐기됐다면 바닥 복귀 시
            // 이어붙이기(SWITCH_TO_LIVE)가 중간 공백을 만들므로 최신 재조회로 복원해야 한다.
            let windowSlid = state.windowSlid;
            if (action.isFirst) {
                // 최초 메시지 로딩: 받은 메시지들을 정렬 후 chatSize만큼만 저장
                updatedMessages = sortMessagesByTimestamp(new Map(action.messages.map(message => [message.uuid, message])));
                while (updatedMessages.size > chatSize) {
                    const oldestKey = updatedMessages.keys().next().value;
                    updatedMessages.delete(oldestKey);
                }
                // 최신 페이지로 전면 교체 — 윈도우는 서버 기준 최신과 연속
                windowSlid = false;
            }
            else {
                // 기존 메시지에 새 메시지들을 추가하고 정렬 후, chatSize만큼만 남김
                updatedMessages = new Map(state.messages);
                action.messages.forEach((message) => {
                    updatedMessages.set(message.uuid, message);
                });
                updatedMessages = sortMessagesByTimestamp(updatedMessages);
                // 서버 조회(REST)분과 기존 실시간 수신분의 키 이원화 중복 억제
                updatedMessages = dedupNearDuplicates(updatedMessages);
                const keys = Array.from(updatedMessages.keys());
                if (updatedMessages.size > chatSize) {
                    if (action.isUp) {
                        // 위로 스크롤 시: 앞쪽(chatSize 이후) 메시지 삭제
                        for (let i = chatSize; i < keys.length; i++) {
                            updatedMessages.delete(keys[i]);
                        }
                        windowSlid = true;
                    }
                    else {
                        // 아래로 스크롤 시: 뒤쪽(chatSize 이전) 메시지 삭제
                        for (let i = 0; i < keys.length - chatSize; i++) {
                            updatedMessages.delete(keys[i]);
                        }
                    }
                }
            }
            return {
                ...state,
                messages: updatedMessages,
                totalCount: action.total_count,
                lastMessage: action.last_message ?? state.lastMessage,
                windowSlid,
                ...(action.isUp ? { isLive: false } : { isLive: state.isLive }),
            };
        }
        case SWITCH_TO_LIVE_MESSAGE: {
            const liveSize = state.liveMessages.size;
            const chatSize = getChatSize();
            let newMessages;
            if (liveSize >= chatSize) {
                // liveMessages가 chatSize 이상일 때: 최신 chatSize개만 표시
                newMessages = new Map();
                const liveArr = Array.from(state.liveMessages.values());
                for (let i = liveArr.length - chatSize; i < liveArr.length; i++) {
                    if (i >= 0) {
                        const msg = liveArr[i];
                        newMessages.set(msg.uuid, msg);
                    }
                }
            }
            else {
                // liveMessages가 chatSize 미만일 때: 기존 messages에서 부족한 만큼 가져와 합치기
                newMessages = new Map();
                const prevArr = Array.from(state.messages.values());
                const startIdx = Math.max(0, prevArr.length - (chatSize - liveSize));
                for (let i = startIdx; i < prevArr.length; i++) {
                    const msg = prevArr[i];
                    newMessages.set(msg.uuid, msg);
                }
                for (const msg of state.liveMessages.values()) {
                    newMessages.set(msg.uuid, msg);
                }
                // timestamp 기준으로 정렬
                const sortedArr = Array.from(newMessages.values()).sort((a, b) => Number(a.timestamp) - Number(b.timestamp));
                newMessages = new Map(sortedArr.map(msg => [msg.uuid, msg]));
                // chatSize 초과 시 오래된 메시지 삭제
                while (newMessages.size > chatSize) {
                    const oldestKey = newMessages.keys().next().value;
                    newMessages.delete(oldestKey);
                }
            }
            // messages(서버 조회분 포함)와 liveMessages(실시간분) 병합 — 키 이원화 중복 억제
            newMessages = dedupNearDuplicates(newMessages);
            const lastMessage = Array.from(newMessages.values()).pop();
            return {
                ...state,
                isLive: true,
                messages: newMessages,
                lastMessage: lastMessage ?? state.lastMessage,
            };
        }
        case 'REMOVE_CHAT_LIST': {
            return {
                ...state,
                messages: new Map(),
                windowSlid: false,
            };
        }
        // case ADD_MESSAGE_LIST: {
        // 	// Create a new Map with the current messages
        // 	const newMessages = new Map(state.messages);
        // 	// Add new messages to the Map
        // 	action.messages.forEach((message: IChatDetail) => {
        // 		newMessages.set(message.uuid, message);
        // 	});
        // 	// Sort messages by timestamp
        // 	const sortedMessages = sortMessagesByTimestamp(newMessages);
        // 	// Determine the range of messages to keep based on isUp
        // 	let updatedMessages = sortedMessages;
        // 	if (action.isUp) {
        // 		// Keep the first 50 messages if isUp is true
        // 		const allMessages = Array.from(sortedMessages.values());
        // 		updatedMessages = new Map(allMessages.slice(0, 50).map(message => [message.uuid, message]));
        // 	} else {
        // 		// Keep the last 50 messages if isUp is false
        // 		const allMessages = Array.from(sortedMessages.values());
        // 		updatedMessages = new Map(allMessages.slice(-50).map(message => [message.uuid, message]));
        // 	}
        // 	return {
        // 		...state,
        // 		messages: updatedMessages,
        // 		totalCount: action.total_count,
        // 		...(action.last_message ? { lastMessage: action.last_message } : {}),
        // 	};
        // }
        case SET_CHOICE_MEMBER:
            return {
                ...state,
                choiceMembers: action.members,
            };
        case SET_IS_CHAT_READ:
            return {
                ...state,
                isRead: action.isRead,
                totalConut: 0,
            };
        case SET_IS_KEYBOARD_OPEN:
            return {
                ...state,
                isKeyboardOpen: action.isKeyboardOpen,
            };
        case SET_IS_LIVE:
            return {
                ...state,
                isLive: action.isLive,
            };
        case LEAVE_ROOM:
            // DEFAULT_STATE 의 Map 인스턴스 공유 오염 방지 — 컬렉션은 새 인스턴스로
            return {
                ...DEFAULT_STATE,
                messages: new Map(),
                liveMessages: new Map(),
                fileList: new Map(),
                choiceMembers: new Map(),
            };
    }
    return state;
});
